import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_PER_RUN_CAP,
  type ProjectSettings,
  type RunDetail,
} from "@zworks/console-contracts";
import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import {
  CertaintyBadge,
  CostPreview,
  EvidenceChecklist,
  PageHeader,
  RunTimeline,
  StatusBadge,
} from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ApiError,
  cancelRun,
  evidenceMatrixQuery,
  flowsQuery,
  flowVersionsQuery,
  pullRequestsQuery,
  runDetailQuery,
  startRun,
  projectSettingsQuery,
  workItemDetailQuery,
} from "@/lib/api";
import { formatCredits, formatDateTime, formatDuration, formatElapsed } from "@/lib/format";
import { useCurrentProject } from "@/lib/project";
import { AskButton } from "@/components/ask-zot";
import { RunStateBadge } from "@/components/bespoke";
import { EvidencePackPanel } from "@/components/evidence-pack";
import { TracePanel } from "@/components/trace-panel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/** A step's elapsed duration, derived from its start and finish timestamps. */
function stepDuration(step: { startedAt: string | null; finishedAt: string | null }): string {
  if (!step.startedAt) return "–";
  const start = Date.parse(step.startedAt);
  if (Number.isNaN(start)) return "–";
  const end = step.finishedAt ? Date.parse(step.finishedAt) : Date.now();
  if (Number.isNaN(end)) return "–";
  return formatDuration(Math.max(0, Math.floor((end - start) / 1000)));
}

/** A step's time span, in 24-hour UTC. */
function stepSpan(step: { startedAt: string | null; finishedAt: string | null }): string {
  const time = (iso: string) => {
    const date = new Date(iso);
    return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
  };
  if (step.startedAt && step.finishedAt)
    return `${time(step.startedAt)} to ${time(step.finishedAt)}`;
  if (step.startedAt) return `waiting since ${time(step.startedAt)}`;
  return "Not started";
}

function DetailError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <ForbiddenState
        title="You do not have access to this project"
        description="Ask an owner to invite you. Nothing from the project is shown."
      />
    );
  }
  const apiError = error instanceof ApiError ? error : null;
  return (
    <ErrorState
      title="Run could not be loaded"
      description={apiError?.problem?.detail ?? "The request could not be completed."}
      requestId={apiError?.requestId ?? "unknown"}
      onRetry={onRetry}
    />
  );
}

function CancelAction({ projectId, detail }: { projectId: string; detail: RunDetail }) {
  const client = useQueryClient();
  const [confirming, setConfirming] = React.useState(false);
  const cancel = useMutation({
    mutationFn: () => cancelRun(projectId, detail.id),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ["projects", projectId, "runs", detail.id],
      });
      void client.invalidateQueries({ queryKey: ["projects", projectId, "runs"] });
      setConfirming(false);
    },
  });
  const ended =
    detail.state === "succeeded" || detail.state === "failed" || detail.state === "cancelled";

  if (ended) return null;
  if (!confirming) {
    return (
      <Button className="min-h-11 sm:min-h-9" variant="outline" onClick={() => setConfirming(true)}>
        Cancel run
      </Button>
    );
  }
  return (
    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      <p className="text-sm text-muted-foreground">Cancelling releases the held credits.</p>
      <Button
        variant="outline"
        className="min-h-11 sm:min-h-9"
        disabled={cancel.isPending}
        onClick={() => {
          cancel.reset();
          setConfirming(false);
        }}
      >
        Keep run
      </Button>
      <Button
        className="min-h-11 sm:min-h-9"
        disabled={cancel.isPending}
        onClick={() => cancel.mutate()}
      >
        {cancel.isPending ? "Cancelling" : "Confirm cancel"}
      </Button>
      {cancel.isError ? (
        <p role="alert" className="w-full text-sm text-danger">
          {cancel.error instanceof ApiError
            ? (cancel.error.problem?.detail ?? cancel.error.message)
            : "The request could not be completed."}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The run detail, as the RunDetail board draws it: the state badge, the current gate, progress,
 * the step timeline, and the four-kind credit panel. An unknown effect stays visible until it
 * is reconciled, and cancelling releases the hold.
 */
export function RunDetailPage() {
  const { project, loaded } = useCurrentProject();
  const { runId } = useParams();
  const detail = useQuery({
    ...runDetailQuery(project?.id ?? "", runId ?? ""),
    enabled: loaded && project !== null && runId !== undefined,
  });
  const evidence = useQuery({
    ...evidenceMatrixQuery(project?.id ?? "", runId ?? ""),
    enabled: loaded && project !== null && runId !== undefined,
  });
  const pullRequests = useQuery({
    ...pullRequestsQuery(project?.id ?? ""),
    enabled: loaded && project !== null && runId !== undefined,
  });

  if (!loaded || (project !== null && runId !== undefined && detail.isPending)) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{project?.name}</p>
        </header>
        <LoadingState label="Loading run" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
        </header>
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its runs appear here."
        />
      </>
    );
  }

  if (runId === undefined) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{project.name}</p>
        </header>
        <EmptyState title="No run selected" description="Choose a run from the runs list." />
      </>
    );
  }

  if (detail.isError) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{project.name}</p>
        </header>
        <DetailError error={detail.error} onRetry={() => void detail.refetch()} />
      </>
    );
  }

  const data = detail.data;
  if (!data) {
    return (
      <>
        <PageHeader title="Run" description={project.name} />
        <LoadingState label="Loading run" rows={6} />
      </>
    );
  }

  const finishedSteps = data.steps.filter((step) => step.finishedAt !== null).length;
  const ended = data.state === "succeeded" || data.state === "failed" || data.state === "cancelled";
  const spendUnknown = data.spendUnknown || data.credits.unknown > 0;
  const knownCredits = spendUnknown ? null : data.credits.confirmed + data.credits.estimated;
  const runPullRequest = (() => {
    if (!pullRequests.isSuccess) return null;
    const byRun = pullRequests.data.find((pr) => pr.runId === data.id);
    if (byRun) return byRun;
    if (!data.branch) return null;
    return pullRequests.data.find((pr) => pr.head === data.branch) ?? null;
  })();

  return (
    <>
      <PageHeader
        title={data.flowVersion ?? `Run ${data.id.slice(0, 8)}`}
        badge={<RunStateBadge state={data.state} />}
        description={
          <>
            {data.flowVersion ? <span className="font-mono">{data.flowVersion}</span> : null}
            {data.flowVersion ? " · " : null}started {formatDateTime(data.startedAt)} by{" "}
            {data.startedBy}
            {data.branch ? (
              <>
                {" "}
                · branch <span className="font-mono">{data.branch}</span>
              </>
            ) : null}
            {runPullRequest?.url ? (
              <>
                {" · "}
                <a
                  className="font-medium text-accent-text underline-offset-4 hover:underline"
                  href={runPullRequest.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Pull request
                </a>
              </>
            ) : null}
          </>
        }
        actions={
          <>
            <AskButton
              kind="run"
              id={data.id}
              label={data.flowVersion ?? data.id.slice(0, 8)}
              variant="outline"
            />
            {!ended ? (
              <Button className="min-h-11 sm:min-h-9" variant="outline" asChild>
                <Link to={`/runs/new?followUp=${encodeURIComponent(data.id)}`}>Follow-up fix</Link>
              </Button>
            ) : null}
            <CancelAction projectId={project.id} detail={data} />
          </>
        }
      />

      {data.state === "waiting" && data.currentGate ? (
        <div
          role="alert"
          className="flex gap-3 rounded-lg border border-warning/40 bg-warning/6 p-3 text-warning"
        >
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium text-foreground">
              Waiting at the gate &ldquo;{data.currentGate}&rdquo;
            </p>
            <p className="text-[0.8125rem] text-muted-foreground">
              Nothing has been deployed. The run continues after an eligible reviewer decides.
              {data.gateExpiresAt
                ? ` The approval expires ${formatDateTime(data.gateExpiresAt)}.`
                : ""}
            </p>
          </div>
        </div>
      ) : null}

      {data.state === "unknown" ? (
        <div
          role="alert"
          className="flex gap-3 rounded-lg border border-dashed border-unknown/60 bg-unknown/6 p-3 text-unknown"
        >
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium text-foreground">Unknown effect</p>
            <p className="text-[0.8125rem] text-muted-foreground">
              This run stays visible until it is reconciled. It is never counted as zero.
            </p>
          </div>
        </div>
      ) : null}

      <section aria-label="Mobile run figures" className="grid grid-cols-2 gap-3 md:hidden">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tracking-tight tabular-nums">
              {data.steps.length === 0
                ? `${data.progress}%`
                : `${finishedSteps} of ${data.steps.length}`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Elapsed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tracking-tight tabular-nums">
              {formatElapsed(data.startedAt)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Credits</CardTitle>
          </CardHeader>
          <CardContent>
            {knownCredits === null ? (
              <CertaintyBadge certainty="unknown" />
            ) : (
              <p className="text-2xl font-semibold tracking-tight tabular-nums">
                {formatCredits(knownCredits)}
              </p>
            )}
            <p className="mt-1 text-[0.8125rem] text-muted-foreground">
              {spendUnknown
                ? "Some spend remains unknown"
                : data.credits.estimated > 0
                  ? "Includes estimated spend"
                  : "Confirmed spend"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Evidence</CardTitle>
          </CardHeader>
          <CardContent>
            {evidence.isPending ? (
              <p className="text-sm text-muted-foreground">Loading</p>
            ) : evidence.isError ? (
              <StatusBadge kind="evidence" status="missing" label="Unknown" />
            ) : (
              <EvidenceChecklist
                runId={data.id}
                signatureStatus="unverified"
                items={evidence.data.cells.map((cell) => ({
                  id: cell.kind,
                  name: cell.kind,
                  detail:
                    cell.state === "verified"
                      ? "Verified"
                      : cell.state === "missing"
                        ? "Missing"
                        : cell.state === "unverified"
                          ? "Unverified"
                          : "Not yet due",
                  status: cell.state,
                }))}
              />
            )}
          </CardContent>
        </Card>
      </section>

      <section
        aria-label="Run figures"
        className="hidden grid-cols-2 gap-3 sm:gap-4 md:grid xl:grid-cols-4"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold tracking-tight tabular-nums">
              {data.steps.length === 0
                ? `${data.progress}%`
                : `${finishedSteps} of ${data.steps.length}`}
            </p>
            <div
              role="progressbar"
              aria-valuenow={data.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Run progress ${data.progress} per cent`}
              className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
            >
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${data.progress}%` }}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Gate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-semibold tracking-tight">{data.currentGate ?? "None"}</p>
            <p className="mt-1 text-[0.8125rem] text-muted-foreground">
              {data.gateExpiresAt
                ? `Expires ${formatDateTime(data.gateExpiresAt)}`
                : "No expiry set"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Credit cap</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold tracking-tight tabular-nums">
              {formatCredits(data.creditCap)}
            </p>
            <p className="mt-1 text-[0.8125rem] text-muted-foreground">
              Held on start, released on cancel
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Started by</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-semibold tracking-tight">{data.startedBy}</p>
            {data.origin && data.origin.kind !== "person" && (
              <p className="mt-1 text-sm text-muted-foreground">
                Started by {data.origin.kind} rule{" "}
                <span className="font-mono">
                  {(data.origin.scheduleId ?? data.origin.triggerId)?.slice(0, 8)}
                </span>
              </p>
            )}

            <p className="mt-1 text-[0.8125rem] text-muted-foreground tabular-nums">
              {formatDateTime(data.startedAt)}
            </p>
          </CardContent>
        </Card>
      </section>

      <Tabs defaultValue="timeline">
        <TabsList aria-label="Run views">
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="trace">Agent Trace</TabsTrigger>
        </TabsList>
        <TabsContent value="timeline">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {data.steps.length === 0 ? (
                  <EmptyState
                    title="No steps yet"
                    description="The run shows Pending until the control plane confirms it."
                  />
                ) : (
                  <>
                    <RunTimeline
                      steps={data.steps.map((step) => ({
                        name: step.name,
                        duration: stepDuration(step),
                        status:
                          step.state === "succeeded"
                            ? "completed"
                            : step.state === "failed" || step.state === "cancelled"
                              ? "failed"
                              : step.state === "pending"
                                ? "pending"
                                : "running",
                      }))}
                      totalDuration={formatElapsed(data.startedAt)}
                    />
                    <ol className="flex flex-col">
                      {data.steps.map((step) => (
                        <li
                          key={step.position}
                          className="flex gap-3.5 border-t py-4 first:border-t-0 first:pt-0 last:pb-0"
                        >
                          <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border text-xs font-medium tabular-nums">
                            {step.position}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-semibold">{step.name}</p>
                              <span className="text-xs text-muted-foreground tabular-nums">
                                {stepSpan(step)}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {step.kind}
                              {step.detail ? ` · ${step.detail}` : ""}
                            </p>
                            <p className="mt-2">
                              <StatusBadge kind="run" status={step.state} />
                            </p>
                            {step.output ? (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-xs text-muted-foreground">
                                  Output
                                </summary>
                                <pre className="mt-1 max-h-72 overflow-auto rounded-md border bg-muted/40 p-2 text-xs whitespace-pre-wrap">
                                  {step.output}
                                </pre>
                              </details>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Credits</CardTitle>
                <CardDescription>Cap {formatCredits(data.creditCap)}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>
                    <CertaintyBadge certainty="confirmed" />
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatCredits(data.credits.confirmed)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>
                    <CertaintyBadge certainty="estimated" />
                  </span>
                  <span className="font-medium tabular-nums">
                    {data.credits.estimated === 0
                      ? "none"
                      : `about ${formatCredits(data.credits.estimated)}`}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>
                    <CertaintyBadge certainty="reserved" />
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatCredits(data.credits.reserved)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>
                    <CertaintyBadge certainty="unknown" />
                  </span>
                  <span className="font-medium tabular-nums">
                    {data.credits.unknown > 0
                      ? formatCredits(data.credits.unknown)
                      : spendUnknown
                        ? "amount unknown"
                        : "none"}
                  </span>
                </div>
                <p className="mt-1 border-t pt-2 text-[0.8125rem] text-muted-foreground">
                  {data.intent}
                </p>
              </CardContent>
            </Card>
            <EvidencePackPanel projectId={project.id} runId={data.id} />
          </div>
        </TabsContent>
        <TabsContent value="trace">
          <TracePanel
            projectId={project.id}
            runId={data.id}
            traceId={data.traceId ?? null}
            running={!ended}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

/** A request key for the start form, shown so a retry is visibly the same request. */
function newIdempotencyKey(): string {
  const bytes = new Uint8Array(8);
  const random =
    typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function"
      ? crypto.getRandomValues(bytes)
      : bytes.map(() => Math.floor(Math.random() * 256));
  return `idem_${Array.from(random, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * The start-run form, as the StartRun board draws it: the work item and flow, the intent, the
 * limits, and the pre-flight panel. Everything is frozen when the run starts. Starting shows
 * Pending until the control plane confirms it.
 */
export function StartRunPage() {
  const { project, loaded } = useCurrentProject();
  const settings = useQuery({
    ...projectSettingsQuery(project?.id ?? ""),
    enabled: Boolean(project),
  });
  if (loaded && project) {
    if (settings.isPending) return <LoadingState label="Loading run defaults" rows={4} />;
    if (settings.isError)
      return (
        <ErrorState
          title="Run defaults could not be loaded"
          description={
            settings.error instanceof ApiError
              ? (settings.error.problem?.detail ?? settings.error.message)
              : "The request could not be completed."
          }
          requestId={
            settings.error instanceof ApiError ? (settings.error.requestId ?? "unknown") : "unknown"
          }
          onRetry={() => void settings.refetch()}
        />
      );
    if (settings.data.status === "archived")
      return (
        <EmptyState
          title="Project archived"
          description="This project is archived and cannot start new runs."
        />
      );
    return <StartRunForm key={project.id} defaults={settings.data} />;
  }
  return <StartRunForm />;
}

function StartRunForm({ defaults }: { defaults?: ProjectSettings }) {
  const { project, loaded } = useCurrentProject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskRequested = searchParams.has("workItem");
  const taskKey = searchParams.get("workItem") ?? "";
  const taskScopeValid = Boolean(
    project && taskKey.trim() && searchParams.get("projectId") === project.id,
  );
  const task = useQuery({
    ...workItemDetailQuery(project?.id ?? "", taskKey),
    enabled: loaded && taskRequested && taskScopeValid,
  });
  const resolvedTask =
    taskScopeValid &&
    task.isSuccess &&
    task.data.projectId === project?.id &&
    task.data.key === taskKey
      ? task.data
      : null;
  const canStart = Boolean(
    project &&
    (project.role === "owner" || project.role === "developer") &&
    (!taskRequested || resolvedTask),
  );
  const [idempotencyKey, setIdempotencyKey] = React.useState(newIdempotencyKey);
  const [flowIdInput, setFlowIdInput] = React.useState<string | null>(null);
  const [flowVersionIdInput, setFlowVersionIdInput] = React.useState<string | null>(null);
  const [pickedFlow, setPickedFlow] = React.useState("");
  const flows = useQuery({ ...flowsQuery(project?.id ?? ""), enabled: Boolean(project) });
  const pickedVersions = useQuery({
    ...flowVersionsQuery(project?.id ?? "", pickedFlow),
    enabled: Boolean(project) && pickedFlow !== "",
  });
  // Choosing a flow defaults both ids to its newest ready version without synchronising state in an effect.
  const latestReadyVersion = [...(pickedVersions.data ?? [])]
    .filter((version) => version.readiness === "ready")
    .sort((a, b) => b.number - a.number)[0];
  const flowId = flowIdInput ?? (latestReadyVersion ? pickedFlow : "");
  const flowVersionId = flowVersionIdInput ?? latestReadyVersion?.id ?? "";
  const pickedVersion =
    pickedVersions.data?.find((version) => version.id === flowVersionId) ??
    latestReadyVersion ??
    null;
  const versionCap = pickedVersion?.workflowCap ?? null;
  const [pinFlowVersion, setPinFlowVersion] = React.useState(true);
  const [intent, setIntent] = React.useState("");
  const [branch, setBranch] = React.useState("");
  const [issueLink, setIssueLink] = React.useState("");
  const [modelTier, setModelTier] = React.useState<"standard" | "deep-reasoning">(
    defaults?.defaultModelTier ?? "standard",
  );
  const [effort, setEffort] = React.useState<"low" | "medium" | "high">(
    defaults?.defaultEffort ?? "medium",
  );
  // A typed cap wins; otherwise the picked version's cap fills the field, else the project default.
  const [capInput, setCapInput] = React.useState<string | null>(null);
  const creditCap =
    capInput ??
    (versionCap != null ? String(versionCap) : String(defaults?.perRunCap ?? DEFAULT_PER_RUN_CAP));
  const [formError, setFormError] = React.useState<string | null>(null);

  const start = useMutation({
    mutationFn: (body: {
      flowId: string;
      flowVersionId: string;
      intent: string;
      branch: string;
      modelTier: "standard" | "deep-reasoning";
      effort: "low" | "medium" | "high";
      creditCap: number;
    }) => {
      if (!project || !canStart) throw new Error("The run cannot be started.");
      return startRun(project.id, {
        workItemId: resolvedTask?.id ?? null,
        flowId: body.flowId,
        flowVersionId: body.flowVersionId,
        pinFlowVersion,
        intent: body.intent,
        branch: body.branch,
        issueLink: issueLink.trim() === "" ? undefined : issueLink.trim(),
        modelTier: body.modelTier,
        effort: body.effort,
        creditCap: body.creditCap,
        idempotencyKey,
      });
    },
    onSuccess: (response) => {
      setIdempotencyKey(newIdempotencyKey());
      void navigate(`/runs/${response.id}`);
    },
    onError: (error) => {
      setFormError(
        error instanceof ApiError
          ? (error.problem?.detail ?? error.message)
          : "The request could not be completed.",
      );
    },
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!canStart || start.isPending) return;
    const cap = Number(creditCap.replace(/,/g, ""));
    if (!flowId.trim() || !flowVersionId.trim()) {
      setFormError("Choose a flow and its version.");
      return;
    }
    if (intent.trim() === "") {
      setFormError("Describe what the run should do.");
      return;
    }
    if (branch.trim() === "") {
      setFormError("Name the branch the run works from.");
      return;
    }
    if (!Number.isInteger(cap) || cap < 1) {
      setFormError("Set a credit cap of at least 1.");
      return;
    }
    start.mutate({
      flowId: flowId.trim(),
      flowVersionId: flowVersionId.trim(),
      intent: intent.trim(),
      branch: branch.trim(),
      modelTier,
      effort,
      creditCap: cap,
    });
  }

  if (!loaded) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Start a run</h1>
        </header>
        <LoadingState label="Loading start form" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Start a run</h1>
        </header>
        <EmptyState
          title="No projects yet"
          description="Create your first project before starting a run."
        />
      </>
    );
  }

  const capNumber = Number(creditCap.replace(/,/g, ""));
  const capValid = Number.isInteger(capNumber) && capNumber >= 1;

  return (
    <>
      <PageHeader title="Start a run" />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Work item and flow</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {taskRequested &&
                (resolvedTask ? (
                  <p className="text-sm">
                    {resolvedTask.key} – {resolvedTask.title}
                  </p>
                ) : (
                  <p role="status" className="text-sm text-muted-foreground">
                    {taskScopeValid && task.isPending
                      ? "Loading work item…"
                      : "This work item is unavailable in the selected project. Return to Work items and choose it again."}
                  </p>
                ))}
              {project.role !== "owner" && project.role !== "developer" && (
                <p className="text-sm text-muted-foreground">
                  Only owners and developers can start runs.
                </p>
              )}
              {flows.data && flows.data.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="start-flow-pick">
                    Pick a flow
                  </label>
                  <select
                    id="start-flow-pick"
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    value={pickedFlow}
                    onChange={(event) => {
                      setPickedFlow(event.target.value);
                      setFlowIdInput(null);
                      setFlowVersionIdInput(null);
                      setCapInput(null);
                    }}
                  >
                    <option value="">Choose a flow…</option>
                    {flows.data
                      .filter((flow) => flow.state === "active" && flow.readiness === "ready")
                      .map((flow) => (
                        <option key={flow.id} value={flow.id}>
                          {flow.name} (v{flow.currentVersion})
                        </option>
                      ))}
                  </select>
                  {pickedFlow && flowVersionId ? (
                    <p className="text-xs text-muted-foreground">Newest ready version selected.</p>
                  ) : null}
                </div>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="start-flow">
                    Flow
                  </label>
                  <Input
                    id="start-flow"
                    value={flowId}
                    onChange={(event) => {
                      setFlowIdInput(event.target.value);
                      setCapInput(null);
                    }}
                    placeholder="Flow id"
                    autoComplete="off"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium text-foreground"
                    htmlFor="start-flow-version"
                  >
                    Flow version
                  </label>
                  <Input
                    id="start-flow-version"
                    value={flowVersionId}
                    onChange={(event) => {
                      setFlowVersionIdInput(event.target.value);
                      setCapInput(null);
                    }}
                    placeholder="Flow version id"
                    autoComplete="off"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={pinFlowVersion}
                  onChange={(event) => setPinFlowVersion(event.target.checked)}
                />
                Pin this version even if a newer version is activated
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>What should it do</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="start-intent">
                  Intent
                </label>
                <Textarea
                  id="start-intent"
                  value={intent}
                  onChange={(event) => setIntent(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="What the run should do"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="start-branch">
                    Branch
                  </label>
                  <Input
                    id="start-branch"
                    value={branch}
                    onChange={(event) => setBranch(event.target.value)}
                    placeholder="feat/refund-idempotency"
                    autoComplete="off"
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">New branch from main</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="start-issue">
                    Issue link <span className="font-normal text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    id="start-issue"
                    value={issueLink}
                    onChange={(event) => setIssueLink(event.target.value)}
                    placeholder="https://tracker.example.com/ORI-214"
                    autoComplete="off"
                    className="font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Limits</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="start-model-tier">
                  Model tier
                </label>
                <Select
                  value={modelTier}
                  onValueChange={(value) => setModelTier(value as "standard" | "deep-reasoning")}
                >
                  <SelectTrigger id="start-model-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="deep-reasoning">Deep reasoning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground" id="start-effort-label">
                  Effort
                </span>
                <div role="group" aria-labelledby="start-effort-label" className="flex gap-2">
                  {(["low", "medium", "high"] as const).map((level) => (
                    <Button
                      key={level}
                      type="button"
                      variant={effort === level ? "chip" : "outline"}
                      size="sm"
                      aria-pressed={effort === level}
                      onClick={() => setEffort(level)}
                    >
                      {level === "low" ? "Low" : level === "medium" ? "Medium" : "High"}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="start-cap">
                  Credit cap for this run
                </label>
                <Input
                  id="start-cap"
                  value={creditCap}
                  onChange={(event) => setCapInput(event.target.value)}
                  inputMode="numeric"
                  autoComplete="off"
                  className="tabular-nums"
                />
                <p className="text-xs text-muted-foreground">
                  {versionCap != null
                    ? `Workflow cap ${versionCap.toLocaleString("en-GB")} credits: the run reserves no more than this.`
                    : "Held now, unused credits are released"}
                </p>
              </div>
            </CardContent>
          </Card>

          {formError ? (
            <p role="alert" className="text-sm text-danger">
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Button type="submit" size="lg" disabled={start.isPending || !canStart}>
              {start.isPending ? "Starting" : "Start run"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              The run shows Pending until the control plane confirms it. Request key{" "}
              <span className="font-mono">{idempotencyKey}</span>
            </p>
          </div>
        </form>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Before you start</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              <p className="flex items-center gap-2">
                <Badge variant="outline">Flow</Badge>
                <span className="text-muted-foreground">
                  {flowVersionId.trim() === "" ? "Choose a version to pin it." : "Version pinned."}
                </span>
              </p>
              <p className="flex items-center gap-2">
                <Badge variant="outline">Branch</Badge>
                <span className="text-muted-foreground">
                  {branch.trim() === "" ? "Name the branch the run works from." : "Branch named."}
                </span>
              </p>
            </CardContent>
          </Card>
          <CostPreview
            cap={capValid ? capNumber : 0}
            currentBalance={project?.creditBalance ?? defaults?.perRunCap ?? DEFAULT_PER_RUN_CAP}
            title="Credits held on start"
          />
        </div>
      </div>
    </>
  );
}
